import React, { useState } from "react";
import { BsXLg } from "react-icons/bs";
import {
  Card,
  CardBody,
  CardTitle,
  Col,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import axios from "axios";
import JSZip from "jszip";
import useOrthophotoStore from "../../store/useOrthophotoStore";

const ModalOrthophoto = ({ toggle, mapRef, layerRefs }) => {
  const {
    missions,
    loadingMissions,
    selectedMissions,
    addSelectedMission,
    removeSelectedMission,
    addMissionsDownloadedKml,
    missionsDownloadedKml,
  } = useOrthophotoStore();

  const [loadingKmzMap, setLoadingKmzMap] = useState({});

  const setMissionLoading = (missionId, isLoading) => {
    setLoadingKmzMap((prev) => ({ ...prev, [missionId]: isLoading }));
  };

  function handleCheckAll() {
    const allMissionsSelected = selectedMissions.length === missions.length;

    if (allMissionsSelected) {
      selectedMissions.forEach((id) => {
        handleMissionChange(id);
      });
    } else {
      const unselectedMissions = missions
        .map(({ id }) => id)
        .filter((id) => !selectedMissions.includes(id));

      unselectedMissions.forEach((id) => {
        handleMissionChange(id);
      });
    }
  }

  const handleMissionChange = async (missionId) => {
    const alreadySelected = selectedMissions.includes(missionId);

    if (alreadySelected) {
      toggleLayerVisibility(missionId, false);
      removeSelectedMission(missionId);
      return;
    }

    addSelectedMission(missionId);

    const mission = missions.find((m) => m.id === missionId);
    if (!mission) return;

    const missionKml = missionsDownloadedKml.find((m) => m[missionId]);
    if (!missionKml) {
      setMissionLoading(missionId, true);
      const downloadedKmz = await downloadKmz(mission);
      const unzippedKmz = await unzipKmz(downloadedKmz, mission);
      if (unzippedKmz) {
        await extractKmlsFromKmz(unzippedKmz);
      }
      setMissionLoading(missionId, false);
    } else {
      toggleLayerVisibility(missionId, true);
    }
  };

  const downloadKmz = async (mission) => {
    try {
      const response = await axios({
        url: mission.path,
        responseType: "blob",
        onDownloadProgress(progress) {},
      });

      const file = new File([response.data], "KMZ");
      const missionKmz = { file, data: mission };

      return missionKmz;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Download cancelled");
      } else {
        console.error("Download error:", error);
      }
      throw error;
    }
  };

  const unzipKmz = (missionKmz) => {
    if (!missionKmz) return Promise.resolve(null);

    return JSZip.loadAsync(missionKmz.file)
      .then((zip) => {
        const objectsZip = Object.entries(zip.files);

        return {
          zip,
          contentKmls: objectsZip,
          data: missionKmz.data,
        };
      })
      .catch((err) => {
        console.error(err);
        if (err.name === "AbortError") {
          console.log("Aborted");
        }
        return null;
      });
  };

  const extractKmlsFromKmz = async (unzippedKmz) => {
    let auxKmlList = {};

    try {
      const contentKmls = unzippedKmz.contentKmls;
      const zip = unzippedKmz.zip;
      const kmzItemActive = unzippedKmz.data;

      for (let kmlPos = 0; kmlPos < contentKmls.length; kmlPos++) {
        const [filePath, fileEntry] = contentKmls[kmlPos];
        const pathParts = filePath.split("/");
        const position = pathParts[0] > 0 ? pathParts[0] : 0;

        if (filePath.endsWith(".kml")) {
          const kmlFileContent = await fileEntry.async("text");
          const converted = kmlFileContent.replaceAll("ns2:", "");

          let imgPathFile = filePath.replace(".kml", ".jpg");
          if (!zip.files[imgPathFile]) {
            imgPathFile = filePath.replace(".kml", ".png");
          }

          let imgSrc = "data:image/jpeg;base64,";

          if (zip.files[imgPathFile]) {
            const imgFileContent = await zip.files[imgPathFile].async("base64");
            imgSrc = `data:image/jpeg;base64,${imgFileContent}`;
          }

          if (!auxKmlList[kmzItemActive.id]) auxKmlList[kmzItemActive.id] = [];
          if (!auxKmlList[kmzItemActive.id][position])
            auxKmlList[kmzItemActive.id][position] = [];

          auxKmlList[kmzItemActive.id][position].push([
            filePath,
            converted,
            imgSrc,
          ]);
        }
      }

      addMissionsDownloadedKml(auxKmlList);
    } catch (error) {
      console.error("Error extracting KMLs from KMZs", error);
    }
  };

  const toggleLayerVisibility = (missionId, visible) => {
    const existingLayers = layerRefs.current[missionId];

    if (!visible) {
      if (existingLayers) {
        existingLayers.forEach((layer) => {
          mapRef.removeLayer(layer);
        });
      }
    } else {
      existingLayers.forEach((layer) => {
        mapRef.addLayer(layer);
      });
    }
  };

  return (
    <>
      <Card
        className="modal-pole-components-card"
        style={{
          left: "120px",
          top: "95px",
          position: "absolute",
          zIndex: "1030",
          display: "block",
          overflowX: "scroll",
          maxHeight: "85%",
          maxWidth: "50%",
        }}
      >
        <CardTitle
          style={{
            position: "sticky",
            top: 0,
            zIndex: "2000",
            backgroundColor: "white",
            margin: "0",
          }}
        >
          <div className="d-flex align-items-center p-3 gap-1">
            <Col style={{ fontSize: "1.2em" }}>Ortophoto</Col>
            <BsXLg onClick={toggle} style={{ cursor: "pointer" }} />
          </div>
          <hr className="m-0" />
        </CardTitle>
        <CardBody>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {!loadingMissions && (
              <>
                <div className="form-check">
                  <Input
                    type="checkbox"
                    id="mission-all"
                    name="all-missions"
                    value=""
                    checked={selectedMissions.length === missions.length}
                    onChange={handleCheckAll}
                  />
                  <Label htmlFor="mission-all" className="form-check-label">
                    Todas
                  </Label>
                </div>

                {missions?.map(({ id, name }) => (
                  <div key={id} className="form-check">
                    <Input
                      type="checkbox"
                      id={`mission-${id}`}
                      name={name}
                      value={id}
                      checked={selectedMissions?.includes(id)}
                      onChange={() => {
                        handleMissionChange(id);
                      }}
                    />
                    {loadingKmzMap[id] && <Spinner size="sm" color="primary" />}
                    <Label
                      htmlFor={`mission-${id}`}
                      className="form-check-label"
                      style={loadingKmzMap[id] ? { marginLeft: ".5rem" } : {}}
                    >
                      {name}
                    </Label>
                  </div>
                ))}
              </>
            )}

            {loadingMissions && (
              <div className="m-auto text-center">
                <Spinner
                  color="primary"
                  style={{
                    margin: "auto",
                    width: "100px",
                    height: "100px",
                  }}
                >
                  Loading...
                </Spinner>
              </div>
            )}

            {!loadingMissions && missions.length === 0 && (
              <div className="m-auto text-center">
                <span>Missões não encontradas!</span>
              </div>
            )}
          </div>
        </CardBody>
      </Card>
    </>
  );
};

export default ModalOrthophoto;
